import { Button, Form } from 'react-bootstrap';
import { CornerDownRight, RefreshCw } from 'react-feather';
import set from 'lodash/set';
import { getRoaringPersonInfo } from '../../../../api/service/superAdmin/company-clients/service';
import PagesHeader from '../../../../components/base/PagesHeader';
import { isEmpty, splitFullName } from '../../../../util/util';
import { FormProps } from '../types';
import classNames from 'classnames';
import ErrorFeedback from '../../../../components/ErrorFeedback';
import { errorHighlighter } from '../../../../util/form';
import PersonalNumber from '../../../../components/form/inputs/PersonalNumber';
import useLocale from '../../../../hooks/useLocale';
import { useMemo, useState } from 'react';
import usePersonalClientFormHelper from '../hook/usePersonalClientFormHelper';
import { getStatusOptions } from './status-options';
import BaseSelectBox from '../../../../components/base/BaseSelectBox';
import { processName, usePersonalNameDropdown } from '../../../../util/personalNameUtils';

export default function PersonalDetailsForm({ context, formGroup }: FormProps) {
  const { personalDetails, setPersonalDetails, isEditMode, patchPersonalDetailsForm, selectedClient } = context;
  const { localize } = useLocale();
  const [isPersonalNumberValid, setIsPersonalNumberValid] = useState<boolean>(true);
  const [firstNames, setFirstNames] = useState<string[]>([]);
  const { firstName, lastName } = splitFullName(formGroup.values.fullName);

  const { isPersonalDetailsValid } = usePersonalClientFormHelper();
  const hasValidPersonalDetails = isPersonalDetailsValid(formGroup);

  const statusOptions = useMemo(
    () => getStatusOptions(selectedClient?.status || '', hasValidPersonalDetails),
    [selectedClient, hasValidPersonalDetails],
  );

  const getMockData = async () => {
    try {
      const defaultFirstName = formGroup.getFieldMeta('firstName').value as string;
      const { email, address, firstNames: apiFirstNames, lastName } = await getRoaringPersonInfo(personalDetails.personalNumber);
      const processedName = processName({ firstNamesList: apiFirstNames, lastName, defaultFirstName: defaultFirstName || ''  });
      
      patchPersonalDetailsForm({ name : processedName.fullName, address, email, lastName });
      setFirstNames(apiFirstNames || []);
      setIsPersonalNumberValid(true);
    } catch (e) {
      setIsPersonalNumberValid(false);
    }
  };

  const handleChange = (ev) => {
    const _personalDetails = { ...personalDetails };
    const inputName = ev.target.name;
    const inputValue = ev.target.value;

    formGroup.setFieldValue(inputName, inputValue);
    set(_personalDetails, inputName, inputValue);

    setPersonalDetails((value) => ({
      ...value,
      ..._personalDetails,
    }));

    setIsPersonalNumberValid(true);
  };

  const handleFirstNameChange = (nameArgs) => {
    formGroup.setFieldValue('firstName', nameArgs.firstName);
    formGroup.setFieldValue('fullName', nameArgs.fullName);
    
    setPersonalDetails((value) => ({
      ...value,
      firstName: nameArgs.firstName,
      fullName: nameArgs.fullName
    }));
  };

  const firstNameDropdown = usePersonalNameDropdown({
    firstNames,
    selectedFirstName: firstName,
    fullName: formGroup.values.fullName,
    onFirstNameChange: handleFirstNameChange
  });

  return (
    <>
      <PagesHeader pageTitle="Personal details" />
      <div className="cr-form-row">
        <PersonalNumber
          disabled={isEditMode}
          value={personalDetails.personalNumber}
          onChangeHandler={(e) => handleChange(e)}
          field={formGroup.getFieldMeta('personalNumber')}
          isValid={isPersonalNumberValid}
          lazy={true}
        />

        <div>
          <Button
            disabled={isEditMode ? false : isEmpty(personalDetails.personalNumber)}
            onClick={isEmpty(personalDetails.personalNumber) ? undefined : getMockData}
            className="app-btn-primary"
          >
            {isEditMode ? <RefreshCw className="icon-gap-right" /> : <CornerDownRight className="icon-gap-right" />}
            {isEditMode ? localize('refresh') : `${localize('fetch_data')}`}
          </Button>
        </div>
        <div className="full-name-field">
          <Form.Label className="fs-name mb-0 required">{localize('first_name')}</Form.Label>
          {firstNames.length > 0 ? (
            <BaseSelectBox
              options={firstNameDropdown.options}
              value={firstNameDropdown.value}
              onChangeHandler={firstNameDropdown.onChange}
              disabled={false}
            />
          ) : (
            <Form.Control
              disabled
              type="text"
              name="firstName"
              className={classNames(errorHighlighter(formGroup.getFieldMeta('firstName')))}
              value={firstName}
              readOnly
            />
          )}
          <ErrorFeedback field={formGroup.getFieldMeta('firstName')} />
        </div>
        <div className="full-name-field">
          <Form.Label className="fs-name mb-0 required">{localize('last_name')}</Form.Label>
          <Form.Control
            disabled
            type="text"
            name="name"
            className={classNames(errorHighlighter(formGroup.getFieldMeta('lastName')))}
            onChange={(ev) => {
              handleChange(ev);
            }}
            value={lastName}
          />
          <ErrorFeedback field={formGroup.getFieldMeta('lastName')} />
        </div>
        {isEditMode && (
          <div className="status-field">
            <Form.Label className="fs-name mb-0 ">{localize('status')}</Form.Label>
            <Form.Select name="status" value={personalDetails.status} onChange={handleChange}>
              <option value="">{localize('please_select')}</option>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {localize(option.label)}
                </option>
              ))}
            </Form.Select>
          </div>
        )}
      </div>
      <div className="cr-form-row">
        <div className="address-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{localize('address')}</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              name="address.street"
              onChange={(ev) => {
                handleChange(ev);
              }}
              value={formGroup.values.address?.street}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('address.street')))}
            />
            <ErrorFeedback field={formGroup.getFieldMeta('address.street')} />
          </Form.Group>
        </div>
        <div className="post-code-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{localize('postcode')}</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('address.postcode')))}
              value={formGroup.values.address?.postcode}
              name="address.postcode"
            />
            <ErrorFeedback field={formGroup.getFieldMeta('address.postcode')} />
          </Form.Group>
        </div>
        <div className="city-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{localize('city')}</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('address.city')))}
              value={formGroup.values.address?.city}
              name="address.city"
            />
            <ErrorFeedback field={formGroup.getFieldMeta('address.city')} />
          </Form.Group>
        </div>
        <div className="country-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{localize('country')}</Form.Label>
            <Form.Control
              size="lg"
              type="text"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('address.country')))}
              value={formGroup.values.address?.country}
              name="address.country"
            />
            <ErrorFeedback field={formGroup.getFieldMeta('address.country')} />
          </Form.Group>
        </div>
      </div>

      <div className="cr-form-row mb-5">
        <div className="phone-num-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{`${localize('phone_number')} (${localize(
              'work',
            )})`}</Form.Label>
            <Form.Control
              size="lg"
              type="number"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('phoneNumber.personal')))}
              name="phoneNumber.personal"
              value={formGroup.values.phoneNumber?.personal}
            />
            <ErrorFeedback field={formGroup.getFieldMeta('phoneNumber.personal')} />
          </Form.Group>
        </div>
        <div className="phone-num-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0">{`${localize('phone_number')} (${localize('private')})`}</Form.Label>
            <Form.Control
              size="lg"
              type="number"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('phoneNumber.private')))}
              name="phoneNumber.private"
              value={formGroup.values.phoneNumber?.private}
            />
            <ErrorFeedback field={formGroup.getFieldMeta('phoneNumber.private')} />
          </Form.Group>
        </div>
        <div className="email-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0 required">{`${localize('email_address')} (${localize(
              'work',
            )})`}</Form.Label>
            <Form.Control
              size="lg"
              type="email"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('email')))}
              name="email"
              value={formGroup.values.email}
            />
            <ErrorFeedback field={formGroup.getFieldMeta('email')} />
          </Form.Group>
        </div>
        <div className="email-field">
          <Form.Group>
            <Form.Label className="fs-name mb-0">{`${localize('alternate_email_address')} (${localize(
              'private',
            )})`}</Form.Label>
            <Form.Control
              size="lg"
              type="email"
              onChange={(ev) => {
                handleChange(ev);
              }}
              className={classNames(errorHighlighter(formGroup.getFieldMeta('alternateEmail')))}
              name="alternateEmail"
              value={formGroup.values.alternateEmail}
            />
            <ErrorFeedback field={formGroup.getFieldMeta('alternateEmail')} />
          </Form.Group>
        </div>
      </div>
    </>
  );
}
